import React, { Dispatch, SetStateAction, useState } from 'react';
import VoxelCard from '../../components/voxel/Cards/VoxelCard';
import VoxelAlertDialog from '../../components/voxel/Dialogs/VoxelAlertDialog';
import VoxelDialog from '../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { DomainTypeGq } from '../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import VoxelTooltipShort from '../../components/voxel/Tooltip/VoxelTooltipShort';

export enum ContentPlanTypeGq {
  TAG = 'TAG',
  RANK = 'RANK',
  COMBINED = 'COMBINED',
}

export interface NewContentPlanDialogProps {
  project: ProjectGq;
  unfinishedScrape: number;
  setUseTagsOpen: Dispatch<SetStateAction<boolean>>;
  setCombiOpen: Dispatch<SetStateAction<boolean>>;
  setProcessingOpen: Dispatch<SetStateAction<boolean>>;
  reload: () => Promise<unknown>;
  hasExistingPlan: boolean;
  newContentPlanOpen: boolean;
  setNewContentPlanOpen: Dispatch<SetStateAction<boolean>>;
}

export const NewContentPlanDialog: React.FC<NewContentPlanDialogProps> = ({
  project,
  unfinishedScrape,
  setUseTagsOpen,
  setCombiOpen,
  setProcessingOpen,
  reload,
  hasExistingPlan,
  newContentPlanOpen,
  setNewContentPlanOpen,
}) => {
  const [contentPlanType, setContentPlanType] = useState<ContentPlanTypeGq>(null);

  // Show warning
  const [showReplaceWarning, setShowReplaceWarning] = useState(hasExistingPlan);

  // Create rank based plan
  const createRankBasedPlanMutation =
    projectsApiService.mutations.contentPlan.useInitiateRunFromScrape();
  const handleCreatePlan = () => {
    createRankBasedPlanMutation({ projectId: project.id })
      .then(reload)
      .then(() => setProcessingOpen(true))
      .finally(() => setNewContentPlanOpen(false));
  };

  // Has serp
  const projectHasSerp = !!project.progressHasSerp;

  // Has client domain
  const projectClientDomain = project.domains?.find((d) => d.type == DomainTypeGq.PROJECT);

  // Trigger scrape
  const hasNoScrape = projectClientDomain?.scrapeTriggered == false;

  const handleCloseCreateContentPlan = () => {
    setNewContentPlanOpen(false);
    setContentPlanType(null);
  };

  return (
    <>
      {showReplaceWarning && newContentPlanOpen && (
        <VoxelAlertDialog
          open={showReplaceWarning && newContentPlanOpen}
          onClose={handleCloseCreateContentPlan}
          severity="warning"
          title="Warning"
        >
          <VoxelDialogContent classMinWidth={'min-w-[500px]'}>
            <VoxelText variant="bread-medium" paddingTop>Existing content plan will be replaced</VoxelText>
          </VoxelDialogContent>
          <VoxelDialogActions>
            <VoxelDialogActionCancel onClick={handleCloseCreateContentPlan} />
            <VoxelDialogActionSubmit title="Proceed" onClick={() => setShowReplaceWarning(false)} />
          </VoxelDialogActions>
        </VoxelAlertDialog>
      )}
      {!showReplaceWarning && newContentPlanOpen && (
        <VoxelDialog
          open={!showReplaceWarning && newContentPlanOpen}
          onClose={handleCloseCreateContentPlan}
          title="Select a content plan option"
        >
          <VoxelDialogContent className="py-12 mb-4 min-w-[1040px]">
            <div className="w-full flex flex-row gap-2 items-stretch justify-evenly ">
              <VoxelCard
                shadowClass="shadow-none"
                className={`p-6 w-72 border ${contentPlanType === ContentPlanTypeGq.RANK ? 'ring-2 ring-voxel-blue-300' : ''
                  }`}
                onClick={() => setContentPlanType(ContentPlanTypeGq.RANK)}
                disabled={
                  !projectClientDomain || !projectHasSerp || hasNoScrape || !!unfinishedScrape
                }
              >
                <div className="flex flex-col gap-3">
                  <VoxelText variant="title3" uppercase className="font-black">
                    Rank-based plan
                  </VoxelText>
                  <VoxelText>
                    Map keywords to the best ranking url among the SERP results.
                  </VoxelText>
                  <VoxelText>
                    Non-ranking keywords will get their own urls with the recommended action{' '}
                    <span className="uppercase font-semibold text-voxel-gray-400">create</span>.
                  </VoxelText>
                </div>
              </VoxelCard>
              <VoxelCard
                shadowClass="shadow-none"
                className={`p-6 w-72 border ${contentPlanType === ContentPlanTypeGq.TAG ? 'ring-2 ring-voxel-blue-300' : ''
                  }`}
                onClick={() => setContentPlanType(ContentPlanTypeGq.TAG)}
                disabled={
                  !project.tagGroups.length ||
                  !projectClientDomain ||
                  !projectHasSerp ||
                  hasNoScrape ||
                  !!unfinishedScrape
                }
              >
                <div className="flex flex-col gap-3">
                  <VoxelText variant="title3" uppercase className="font-black">
                    Tag-based plan
                  </VoxelText>
                  <VoxelText>Map keywords to urls created from tag group combinations.</VoxelText>
                  <VoxelText>Untagged keywords will be excluded from the plan.</VoxelText>
                </div>
              </VoxelCard>
              <VoxelCard
                shadowClass="shadow-none"
                className={`p-6 w-72 border ${contentPlanType === ContentPlanTypeGq.COMBINED ? 'ring-2 ring-voxel-blue-300' : ''
                  }`}
                tooltip="Project must have a tag structure. Create a tag structure first"
                disableTooltip={!!project.tagGroups?.length}
                onClick={() => setContentPlanType(ContentPlanTypeGq.COMBINED)}
                disabled={
                  !project.tagGroups.length ||
                  !projectClientDomain ||
                  !projectHasSerp ||
                  hasNoScrape ||
                  !!unfinishedScrape
                }
              >
                <div className="flex flex-col gap-3">
                  <VoxelText variant="title3" uppercase className="font-black">
                    Combination plan
                  </VoxelText>
                  <VoxelText>
                    Cluster keywords based on tag group combinations and then map them to the best
                    ranking url among the clustered keywords.
                  </VoxelText>
                  <VoxelText>
                    Non-ranking keyword clusters will get their own urls created from tag group
                    combinations with{' '}
                    <span className="uppercase font-semibold text-voxel-gray-400">create</span> as
                    recommended action. Untagged keywords will be excluded from the plan.
                  </VoxelText>
                </div>
              </VoxelCard>
            </div>
          </VoxelDialogContent>
          <VoxelDialogActions>
            <VoxelDialogActionCancel onClick={handleCloseCreateContentPlan} />
            <VoxelDialogActionSubmit
              title="Create plan"
              disabled={contentPlanType === null}
              onClick={() => {
                if (contentPlanType === ContentPlanTypeGq.RANK) {
                  handleCreatePlan();
                }
                if (contentPlanType === ContentPlanTypeGq.TAG) {
                  setUseTagsOpen(true);
                }
                if (contentPlanType === ContentPlanTypeGq.COMBINED) {
                  setCombiOpen(true);
                }
                handleCloseCreateContentPlan();
              }}
            />
          </VoxelDialogActions>
        </VoxelDialog>
      )}
    </>
  );
};

export default NewContentPlanDialog;
